"use client";

import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { SupportedLanguage } from "@/lib/shared";
import lang from "@/app/[locale]/(main)/me/lang.json";

type TimeSlotSelectorProps<T = string[]> = {
  selectedSlots?: T;
  onSlotsChange?: (slots: T) => void;
  availableSlots?: string[];
  locale: SupportedLanguage;
};

const timeSlots = [
  "00:00",
  "01:00",
  "02:00",
  "03:00",
  "04:00",
  "05:00",
  "06:00",
  "07:00",
  "08:00",
  "09:00",
  "10:00",
  "11:00",
  "12:00",
  "13:00",
  "14:00",
  "15:00",
  "16:00",
  "17:00",
  "18:00",
  "19:00",
  "20:00",
  "21:00",
  "22:00",
  "23:00",
];

export function TimeSlotSelector<T extends string | string[]>({
  selectedSlots,
  onSlotsChange,
  availableSlots,
  locale,
}: TimeSlotSelectorProps<T>) {
  // const [activePeriod, activePeriodSet] = useState<"AM" | "PM">("AM");

  // Determine selection mode based on the type parameter T
  // If T extends string (not string[]), we're in single selection mode
  const isSingleSelection = !Array.isArray(selectedSlots);

  // Normalize selectedSlots to always be an array for internal state
  const normalizeSlots = useCallback((slots: T | undefined): string[] => {
    if (!slots) return [];
    return Array.isArray(slots) ? slots : [slots];
  }, []);

  const [selectedTimes, selectedTimesSet] = useState<string[]>(
    normalizeSlots(selectedSlots)
  );

  // Update internal state when selectedSlots prop changes
  useEffect(() => {
    selectedTimesSet(normalizeSlots(selectedSlots));
  }, [selectedSlots, normalizeSlots]);

  const handleSlotToggle = (slot: string) => {
    // Don't allow selection of unavailable slots
    if (!isSlotAvailable(slot)) return;

    const isSelected = selectedTimes.some((s) => s === slot);

    let newSelectedTimes: string[];

    if (isSingleSelection) {
      newSelectedTimes = [slot];
    } else {
      // For multiple selection, toggle the slot
      if (isSelected) {
        newSelectedTimes = selectedTimes.filter((s) => s !== slot);
      } else {
        newSelectedTimes = [...selectedTimes, slot];
      }
    }

    selectedTimesSet(newSelectedTimes);

    // Return the appropriate type based on the original selectedSlots type
    if (isSingleSelection) {
      onSlotsChange?.(newSelectedTimes[0] as T);
    } else {
      onSlotsChange?.(newSelectedTimes as T);
    }
  };

  const isSlotSelected = (slot: string) => {
    return selectedTimes.some((s) => s === slot);
  };

  const isSlotAvailable = (slot: string) => {
    // If availableSlots is not provided, all slots are available
    if (!availableSlots) return true;
    return availableSlots.includes(slot);
  };

  return (
    <div className="space-y-4">
      <Label>{lang["available-time-slots"][locale]}</Label>
      <div className="grid grid-cols-4 gap-3">
        {timeSlots.map((slot) => {
          const isSelected = isSlotSelected(slot);
          const isAvailable = isSlotAvailable(slot);
          return (
            <Button
              type="button"
              key={slot}
              variant={isSelected ? "default" : "outline"}
              size="sm"
              disabled={!isAvailable}
              onClick={() => handleSlotToggle(slot)}
              className={cn(
                "h-10",
                isSelected && "bg-black text-white hover:bg-gray-800"
              )}
            >
              {slot}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
